import dayjs from "dayjs";
import React from "react";
import CircleIcon from "@mui/icons-material/Circle";
import StatusBadge from "../../../../../common/StatusBadge";
import { GridColDef } from "@mui/x-data-grid";
import { useLanguageStore } from "../../../../../store/useLanguageStore";
import { TicketFinancialType } from "../../../../../enums/ticketFinancialType";

export let hideColumns: string[] = [
  "id",
  "statusId",
  "categoryId",
  "interventionTypeId",
  "locationId",
  "priorityId",
  "customerId",
  "contractorId",
  "assignedToId",
  "departmentId",
  "financialAssignedToId",
  "financialAssignedTo",
  "ticketFinancialType",
  "author",
  "authorId",
  "description",
  "interventionType",
  "contractor",
  "updatedDate",
  "location",
  "department",
  "companyId",
];

export const useTicketColumns = (): GridColDef[] => {
  const { language } = useLanguageStore();

  return [
    {
      field: "id",
      headerName: "Id",
      type: "string",
      editable: false,
    },
    {
      field: "ticketNo",
      headerName: "Ticket No.",
      type: "string",
      editable: false,
      width: 150,
      renderCell: ({ row }) => (
        <div className="position-relative">
          <span
            className="text-truncate"
            title={row.ticketNo ? `${row.ticketNo} ${row.isMasterTicket ? "- Master Ticket" : ""}` : ""}
            style={{ maxWidth: 150, height: 40 }}
          >
            {row.ticketNo ?? ""}
          </span>
          {row.isMasterTicket && (
            <CircleIcon sx={{ position: "absolute", right: 20, bottom: 17, width: 8, height: 8, color: "#53c82f" }} />
          )}
        </div>
      ),
    },
    {
      field: "title",
      headerName: "Title",
      type: "string",
      editable: false,
      width: 450,
      renderCell: ({ row }) => (
        <div className="d-flex align-items-center justify-content-between">
          <span className="text-truncate" title={row.title ?? ""} style={{ maxWidth: 450 }}>
            {row.title}
          </span>
        </div>
      ),
    },
    {
      field: "author",
      headerName: "Author",
      type: "string",
      editable: false,
      renderCell: ({ row }) => {
        const author = row.author ? `${row.author?.firstname ?? ""} ${row.author?.lastname ?? ""}` : "";
        return <span title={author}>{author}</span>;
      },
    },
    {
      field: "assignedTo",
      headerName: "Assigned to",
      type: "string",
      editable: false,
      width: 100,
      renderCell: ({ row }) => <CustomCellAssignedTo row={row} />,
    },
    {
      field: "ticketFinancialType",
      headerName: "TicketFinancialType",
      type: "custom",
      editable: false,
      renderCell: ({ row }) => {
        const financialType = TicketFinancialType[row.ticketFinancialType];
        const title = language[financialType] ?? financialType;
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "financialAssignedTo",
      headerName: "FinancialAssignedTo",
      type: "string",
      editable: false,
      width: 150,
      renderCell: ({ row }) => {
        const title = row.financialAssignedTo
          ? `${row.financialAssignedTo?.firstname ?? ""} ${row.financialAssignedTo?.lastname ?? ""}`
          : "";

        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "company",
      headerName: "Company",
      type: "string",
      editable: false,
      width: 100,
      renderCell: ({ row }) => {
        const title = row.company ? row.company?.title ?? "" : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "description",
      headerName: "Description",
      type: "string",
      editable: false,
    },
    {
      field: "category",
      headerName: "Category",
      type: "string",
      editable: false,
      width: 100,
      renderCell: ({ row }) => {
        const title = row.category ? row.category.title : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "department",
      headerName: "Department",
      type: "string",
      width: 100,
      editable: false,
      renderCell: ({ row }) => {
        const title = row.department ? row.department.title : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "interventionType",
      headerName: "InterventionType",
      type: "string",
      editable: false,
      renderCell: ({ row }) => {
        const title = row.interventionType ? row.interventionType.title : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "location",
      headerName: "Location",
      type: "string",
      editable: false,
      renderCell: ({ row }) => {
        const title = row.location ? row.location.title : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "priority",
      headerName: "Priority",
      type: "string",
      editable: false,
      width: 100,
      renderCell: ({ row }) => {
        const title = row.priority ? row.priority.title : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "customer",
      headerName: "Customer",
      type: "string",
      width: 150,
      editable: false,
      renderCell: ({ row }) => {
        const title = row.customer ? row.customer.title : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "phoneNo",
      headerName: "PhoneNo",
      type: "string",
      width: 150,
      editable: false,
      renderCell: ({ row }) => {
        const title = row.customer ? row.customer.phoneNo : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "contractor",
      headerName: "Contractor",
      type: "string",
      editable: false,
      renderCell: ({ row }) => {
        const title = row.contractor ? row.contractor.title : "";
        return <span title={title}>{title}</span>;
      },
    },
    {
      field: "dueDate",
      headerName: "DueDate",
      type: "string",
      editable: false,
      width: 100,
      renderCell: ({ row }) => <span>{row.dueDate ? dayjs(row.dueDate).format("DD/MM/YYYY") : undefined}</span>,
    },
    {
      field: "updatedDate",
      headerName: "UpdatedDate",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.updatedDate ? dayjs(row.updatedDate).format("DD/MM/YYYY") : undefined}</span>,
    },
    {
      field: "isRecurring",
      headerName: "Is Recurring",
      type: "string",
      editable: false,
      width: 50,
      renderCell: ({ row }) => <span>{row.isRecurring ? language["Yes"] : language["No"]}</span>,
    },
    {
      field: "status",
      headerName: "Status",
      type: "string",
      editable: true,
      width: 200,
      cellClassName: "d-flex align-items-center",
      renderCell: ({ row }) => (
        <StatusBadge
          fontSize={12}
          width={"fit-content"}
          color={row.status?.badgeColor || "#8D97A0"}
          name={row.status ? row.status.title : "No Status"}
        />
      ),
    },
    {
      field: "statusId",
      headerName: "StatusId",
      type: "custom",
      editable: false,
    },
    {
      field: "companyId",
      headerName: "CompanyId",
      type: "custom",
      editable: false,
    },
    {
      field: "categoryId",
      headerName: "CategoryId",
      type: "custom",
      editable: false,
    },
    {
      field: "departmentId",
      headerName: "DepartmentId",
      type: "custom",
      editable: false,
    },
    {
      field: "interventionTypeId",
      headerName: "InterventionTypeId",
      type: "custom",
      editable: false,
    },
    {
      field: "locationId",
      headerName: "LocationId",
      type: "custom",
      editable: false,
    },
    {
      field: "priorityId",
      headerName: "PriorityId",
      type: "custom",
      editable: false,
    },
    {
      field: "customerId",
      headerName: "CustomerId",
      type: "custom",
      editable: false,
    },
    {
      field: "assignedToId",
      headerName: "AssignedToId",
      type: "custom",
      editable: false,
    },
    {
      field: "contractorId",
      headerName: "ContractorId",
      type: "custom",
      editable: false,
    },
    {
      field: "financialAssignedToId",
      headerName: "FinancialAssignedToId",
      type: "custom",
      editable: false,
    },
  ];
};

const CustomCellAssignedTo = React.memo(
  ({ row }: any) => {
    const assignedTo = row.assignedTo;
    const fullName = assignedTo ? `${assignedTo.firstname ?? ""} ${assignedTo.lastname ?? ""}`.trim() : "";

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <span title={fullName}>{fullName}</span>
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.row.assignedToId === nextProps.row.assignedToId
);

export default useTicketColumns;
